/**
 * Help & Support text. A first draft: edit freely. Guides open as short articles; each FAQ is a
 * question with its answer. Search on the Help page looks through all of it.
 */

export interface Guide {
  id: string;
  title: string;
  summary: string;
  icon: string;
  steps: { title: string; body: string }[];
}

export const GUIDES: Guide[] = [
  {
    id: "getting-started",
    title: "Getting started",
    summary: "Set up your store and make your first sale.",
    icon: "heroicons:rocket-launch",
    steps: [
      { title: "Add your products", body: "Go to Add Product. Give each product a clear name, a price, a photo and a short description. Good photos sell." },
      { title: "Make the store yours", body: "In Web Builder, add your logo, banner images and colours, and check how your store looks on a phone." },
      { title: "Set your fees", body: "Under Checkout Fees, add anything customers pay on top of the product, like packaging or takeaway." },
      { title: "Share your link", body: "Copy your store link from the dashboard and share it on WhatsApp, Instagram, TikTok and anywhere your customers are." },
    ],
  },
  {
    id: "meta-ads",
    title: "Running Meta Ads",
    summary: "Boost your store or a product on Facebook and Instagram.",
    icon: "heroicons:megaphone",
    steps: [
      { title: "Start a boost", body: "Click Boost on Meta Ads on the dashboard, the Meta Ads page or next to any product." },
      { title: "Choose what to promote", body: "Pick your whole storefront or a single product. Products with good photos usually do best." },
      { title: "Add your creative", body: "Upload your own photos or videos, or let our team create the ad for you." },
      { title: "Pick placements and budget", body: "Choose where the ad shows, your total budget and how many days it runs, then pay with Paystack." },
      { title: "Watch the results", body: "Our team sets up the ad on Meta. Views, clicks, carts and purchases appear on the Meta Ads page as the ad runs." },
    ],
  },
  {
    id: "orders",
    title: "Orders & delivery",
    summary: "What happens when a customer buys.",
    icon: "heroicons:truck",
    steps: [
      { title: "A customer pays", body: "Customers pay online at checkout. The order then appears on your Orders page and you get a WhatsApp message." },
      { title: "Prepare the order", body: "Get the items ready for pickup by the delivery rider." },
      { title: "Delivery", body: "A rider collects the order from your address and takes it to the customer." },
      { title: "Mark it delivered", body: "Once the customer has it, mark the order as delivered on the Orders page." },
    ],
  },
  {
    id: "payouts",
    title: "Payouts & fees",
    summary: "How you get paid for your sales.",
    icon: "heroicons:banknotes",
    steps: [
      { title: "Add your bank account", body: "In your Profile, add the account you want your sales paid into." },
      { title: "What you receive", body: "You receive the price of your products plus any checkout fees you set. Delivery is paid to the rider." },
      { title: "Questions about a payout", body: "Chat with us or email support with the order details and we'll look into it." },
    ],
  },
];

export const FAQS: { question: string; answer: string }[] = [
  {
    question: "How do I share my store?",
    answer: "Copy your store link from the top of the dashboard and share it on WhatsApp, Instagram, TikTok or anywhere else. Each product also has its own link you can share from your store.",
  },
  {
    question: "How much does a Meta Ads boost cost?",
    answer: "You choose the budget, from ₦5,000. The whole budget goes towards showing your ad, and you can see how it was spent on the Meta Ads page.",
  },
  {
    question: "How long until my ad is live?",
    answer: "After you pay, our team builds the ad and sends it to Meta for review. Your campaign shows as “Setting up” until it goes live, then “Active”.",
  },
  {
    question: "Can I pause an ad or change its budget?",
    answer: "Yes. Open the campaign on the Meta Ads page and choose Pause or Edit budget. Our team applies the change on Meta for you.",
  },
  {
    question: "What is a Meta Pixel and do I need one?",
    answer: "A Pixel lets Meta see what people do on your store after they click an ad. You only need your own if you run ads yourself in Meta Ads Manager. Boosts you buy through Storbolt are tracked for you.",
  },
  {
    question: "How do customers pay?",
    answer: "Customers pay online at checkout with card, bank transfer or USSD through Paystack.",
  },
  {
    question: "Can I sell products that take a few days to make?",
    answer: "Yes. When adding a product, choose “Made to order” and set how many days it takes. Customers see when it will be ready.",
  },
  {
    question: "How do I change how my store looks?",
    answer: "Use Web Builder to change your logo, banners, colours and the text on your store.",
  },
];
